import React from "react";
import { Navigate } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import { CircularProgress, Box } from "@mui/joy";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requirePasswordSet?: boolean; // Password gate: redirect invited users who haven't set a password
  requireUsername?: boolean; // Prep for OAuth onboarding in Phase 2
  requireApproval?: boolean; // Waitlist gate: redirect pending users to /waitlist
  requireOnboardingComplete?: boolean; // Onboarding gate: redirect to complete-profile
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requirePasswordSet = true,
  requireUsername = true,
  requireApproval = true,
  requireOnboardingComplete = true,
}) => {
  const {
    session,
    profile,
    initialized,
    loading,
    profileLoading,
    isPasswordSet,
    isWaitlisted,
    isOnboardingComplete,
  } = useAuthStore();

  // Wait for auth initialization and profile loading
  if (!initialized || loading || profileLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Not authenticated -> landing page
  if (!session) {
    return <Navigate to="/" replace />;
  }

  // Invited user hasn't set a password yet -> set password page
  if (requirePasswordSet && !isPasswordSet()) {
    return <Navigate to="/auth/reset-password?flow=invite" replace />;
  }

  // Authenticated but waitlisted -> waitlist page
  if (requireApproval && isWaitlisted()) {
    return <Navigate to="/waitlist" replace />;
  }

  // Authenticated but missing username (OAuth user) -> onboarding (Phase 2)
  if (requireUsername && !profile?.username) {
    return <Navigate to="/onboarding" replace />;
  }

  // Authenticated but onboarding not completed -> complete profile
  if (requireOnboardingComplete && !isOnboardingComplete()) {
    return <Navigate to="/onboarding/complete-profile" replace />;
  }

  return <>{children}</>;
};
